import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Upload, FileText, CheckCircle2, AlertCircle, Clock, FileSpreadsheet, History, Mail, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

function fmtCLP(n) {
  if (n === null || n === undefined) return '$ 0';
  return Number(n).toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });
}

function fmtDate(iso) {
  if (!iso) return '—';
  return iso.slice(0, 19).replace('T', ' ');
}

export default function RCVImportPage() {
  const [archivoCompra, setArchivoCompra] = useState(null);
  const [archivoVenta, setArchivoVenta] = useState(null);
  const [periodo, setPeriodo] = useState('');
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [stats, setStats] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [scanStatus, setScanStatus] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const [s, h, ss] = await Promise.all([
        api.get('/contabilidad/rcv/provisionales-stats'),
        api.get('/contabilidad/rcv/historial?limit=10'),
        api.get('/contabilidad/rcv/scan-status'),
      ]);
      setStats(s.data);
      setHistorial(h.data.items || []);
      setScanStatus(ss.data || null);
    } catch {
      // silencioso
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const handleScanMailbox = async (includeRead = false) => {
    setScanning(true);
    try {
      const url = includeRead
        ? '/contabilidad/rcv/scan-mailbox?include_read=true'
        : '/contabilidad/rcv/scan-mailbox';
      const res = await api.post(url, {});
      const data = res.data || {};

      // Background mode (include_read=true): hacer polling al status
      if (data.background) {
        toast.info('🔄 Relectura iniciada en segundo plano. Esto puede tomar 1-3 min según cantidad de correos.');
        const startTs = Date.now();
        const pollUntilDone = async () => {
          while (Date.now() - startTs < 5 * 60 * 1000) {  // max 5 min
            await new Promise(r => setTimeout(r, 4000));
            try {
              const ss = await api.get('/contabilidad/rcv/scan-status');
              const st = ss.data?.last_status || {};
              if (!st.running && st.completed_at) {
                const totalIns = (st.compra_insertados || 0) + (st.venta_insertados || 0);
                if (totalIns > 0) {
                  toast.success(`✅ Relectura completa: ${totalIns} provisionales nuevos desde ${st.emails_procesados_ok} correo(s).`);
                } else if ((st.emails_revisados || 0) > 0) {
                  toast.info(`✅ Relectura completa: ${st.emails_revisados} correos revisados, sin RCV nuevos para importar.`);
                } else {
                  toast.info('✅ Relectura completa: no hay correos en el rango.');
                }
                if (st.status === 'error') {
                  toast.error(`Scan falló: ${st.error || 'desconocido'}`);
                }
                loadStats();
                return;
              }
            } catch {/* sigue intentando */}
          }
          toast.warning('Polling timeout. Revisa el dashboard para ver el resultado final.');
          loadStats();
        };
        pollUntilDone().finally(() => setScanning(false));
        return;
      }

      if (!data.ok) {
        toast.error(`Scan fallido: ${data.message || data.reason || 'desconocido'}`);
        return;
      }
      const totalIns = (data.compra_insertados || 0) + (data.venta_insertados || 0);
      if (totalIns > 0) {
        toast.success(`Scan completo: ${totalIns} provisionales nuevos desde ${data.emails_procesados_ok} correo(s)`);
      } else if ((data.emails_revisados || 0) > 0) {
        toast.info(`${data.emails_revisados} correos revisados. Sin RCV nuevos para importar.`);
      } else {
        toast.info('No hay correos nuevos en el buzón.');
      }
      loadStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al disparar scan');
    } finally {
      // Solo reseteamos scanning aquí en modo NO background.
      // En background lo hace el polling en su propio finally.
      // Para el caso default no-background, ya retornamos ok por aquí.
      setScanning(false);
    }
  };

  const handleSubmit = async () => {
    if (!archivoCompra && !archivoVenta) {
      toast.error('Sube al menos un archivo (compra o venta)');
      return;
    }
    setLoading(true);
    setResultado(null);
    const fd = new FormData();
    if (archivoCompra) fd.append('archivo_compra', archivoCompra);
    if (archivoVenta) fd.append('archivo_venta', archivoVenta);
    if (periodo) fd.append('periodo', periodo);

    try {
      const res = await api.post('/contabilidad/rcv/import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setResultado(res.data);
      toast.success('RCV importado correctamente');
      setArchivoCompra(null);
      setArchivoVenta(null);
      setPeriodo('');
      // Reset inputs file
      const inputs = document.querySelectorAll('input[type="file"]');
      inputs.forEach(i => { i.value = ''; });
      loadStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al importar RCV');
    } finally {
      setLoading(false);
    }
  };

  const renderResumenLado = (titulo, data) => {
    if (!data) return null;
    return (
      <div className="space-y-2 p-3 rounded-lg border bg-muted/30">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-blue-600" />
          <span className="font-semibold text-sm">{titulo}</span>
          <Badge variant="outline" className="text-[10px]">{data.filename}</Badge>
        </div>
        <div className="grid grid-cols-4 gap-2 text-xs">
          <div className="p-2 rounded bg-emerald-500/10">
            <div className="text-[10px] text-muted-foreground">Insertados</div>
            <div className="text-lg font-bold text-emerald-600 tabular-nums">{data.insertados}</div>
          </div>
          <div className="p-2 rounded bg-amber-500/10">
            <div className="text-[10px] text-muted-foreground">Ya existían (provisional)</div>
            <div className="text-lg font-bold text-amber-600 tabular-nums">{data.ya_existian_provisional}</div>
          </div>
          <div className="p-2 rounded bg-blue-500/10">
            <div className="text-[10px] text-muted-foreground">Ya existían (con XML)</div>
            <div className="text-lg font-bold text-blue-600 tabular-nums">{data.ya_existian_con_xml}</div>
          </div>
          <div className="p-2 rounded bg-red-500/10">
            <div className="text-[10px] text-muted-foreground">Errores</div>
            <div className="text-lg font-bold text-red-600 tabular-nums">{data.errores}</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-5 page-enter" data-testid="rcv-import-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <FileSpreadsheet className="h-6 w-6 text-indigo-600" />
          Importar RCV del SII
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Sube los archivos RCV (Registro de Compras y/o Ventas) descargados del SII.
          Cada fila se inserta como un <strong>DTE Provisional</strong> que entra al flujo
          normal de contabilización aunque aún no tengas el XML descargado.
        </p>
      </div>

      {/* KPIs */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card data-testid="kpi-pendientes">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-[11px] text-muted-foreground">Provisionales pendientes XML</p>
                <p className="text-2xl font-bold tabular-nums">{stats.total_provisionales_pendientes}</p>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="kpi-reconciliados">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-[11px] text-muted-foreground">Reconciliados (XML llegó)</p>
                <p className="text-2xl font-bold tabular-nums">{stats.total_reconciliados}</p>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="kpi-verificados">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-[11px] text-muted-foreground">DTE con XML verificados en RCV</p>
                <p className="text-2xl font-bold tabular-nums">{stats.total_xml_verificados_en_rcv}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Mailbox automático rcv@ */}
      <Card data-testid="mailbox-card" className="border-blue-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="h-4 w-4 text-blue-600" />
            Lectura automática del buzón
            {scanStatus?.configured ? (
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 text-[10px]">
                CONFIGURADO
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">
                NO CONFIGURADO
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Buzón</p>
              <p className="font-mono font-semibold mt-1">{scanStatus?.mailbox || 'rcv@chirinosteel.com'}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Frecuencia</p>
              <p className="mt-1">Cada 30 minutos</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Último scan</p>
              <p className="mt-1 font-mono">{fmtDate(scanStatus?.last_status?.last_run)}</p>
              {scanStatus?.last_status?.status && (
                <Badge
                  variant="outline"
                  className={`mt-1 text-[9px] ${
                    scanStatus.last_status.status === 'success' ? 'border-emerald-300 text-emerald-700' :
                    scanStatus.last_status.status === 'partial' ? 'border-amber-300 text-amber-700' :
                    scanStatus.last_status.status === 'error' ? 'border-red-300 text-red-700' :
                    'border-gray-300 text-gray-600'
                  }`}
                >
                  {scanStatus.last_status.status}
                </Badge>
              )}
            </div>
          </div>

          {scanStatus?.last_status && (
            <div className="grid grid-cols-4 gap-2 text-xs">
              <div className="p-2 rounded bg-blue-500/10">
                <div className="text-[10px] text-muted-foreground">Correos revisados</div>
                <div className="text-lg font-bold tabular-nums">{scanStatus.last_status.emails_revisados ?? 0}</div>
              </div>
              <div className="p-2 rounded bg-emerald-500/10">
                <div className="text-[10px] text-muted-foreground">OK procesados</div>
                <div className="text-lg font-bold text-emerald-600 tabular-nums">{scanStatus.last_status.emails_procesados_ok ?? 0}</div>
              </div>
              <div className="p-2 rounded bg-indigo-500/10">
                <div className="text-[10px] text-muted-foreground">Compra ins.</div>
                <div className="text-lg font-bold text-indigo-600 tabular-nums">{scanStatus.last_status.compra_insertados ?? 0}</div>
              </div>
              <div className="p-2 rounded bg-purple-500/10">
                <div className="text-[10px] text-muted-foreground">Venta ins.</div>
                <div className="text-lg font-bold text-purple-600 tabular-nums">{scanStatus.last_status.venta_insertados ?? 0}</div>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={() => handleScanMailbox(false)}
              disabled={scanning || !scanStatus?.configured}
              variant="outline"
              size="sm"
              className="gap-2"
              data-testid="btn-scan-mailbox"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${scanning ? 'animate-spin' : ''}`} />
              {scanning ? 'Leyendo correos…' : 'Forzar lectura ahora'}
            </Button>
            <Button
              onClick={() => {
                if (window.confirm('Esto va a relectar TODOS los correos de los últimos 14 días, incluso los ya marcados como leídos.\n\n⚠️ Si un RCV ya fue importado antes, los registros ya existentes NO se duplicarán (idempotencia por folio+RUT). Solo se importarán los que falten.\n\n¿Continuar?')) {
                  handleScanMailbox(true);
                }
              }}
              disabled={scanning || !scanStatus?.configured}
              variant="outline"
              size="sm"
              className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="btn-scan-mailbox-include-read"
              title="Releer también correos ya marcados como leídos"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${scanning ? 'animate-spin' : ''}`} />
              Forzar relectura (incluir leídos)
            </Button>
            <p className="text-[11px] text-muted-foreground">
              Reenvía el RCV a <span className="font-mono font-semibold">{scanStatus?.mailbox || 'rcv@chirinosteel.com'}</span> y el sistema lo importa solo.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Upload Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Upload className="h-4 w-4" />
            Cargar archivos RCV
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert className="border-blue-200 bg-blue-50/50">
            <AlertCircle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-xs text-blue-900">
              El sistema es <strong>idempotente</strong>: si ya importaste un período, volver a subirlo
              no duplica nada. Los DTEs provisionales se reconcilian automáticamente cuando llega el XML real.
            </AlertDescription>
          </Alert>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">RCV Compra (CSV)</label>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setArchivoCompra(e.target.files?.[0] || null)}
                className="block w-full text-xs file:mr-2 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                data-testid="input-archivo-compra"
              />
              {archivoCompra && (
                <p className="text-[10px] text-emerald-600 truncate">✓ {archivoCompra.name}</p>
              )}
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">RCV Venta (CSV)</label>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setArchivoVenta(e.target.files?.[0] || null)}
                className="block w-full text-xs file:mr-2 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                data-testid="input-archivo-venta"
              />
              {archivoVenta && (
                <p className="text-[10px] text-emerald-600 truncate">✓ {archivoVenta.name}</p>
              )}
            </div>
          </div>

          <div className="space-y-1 max-w-xs">
            <label className="text-xs font-medium text-muted-foreground">
              Período (opcional, ej: 2026-04)
            </label>
            <input
              type="text"
              placeholder="Auto-detectado del nombre"
              value={periodo}
              onChange={(e) => setPeriodo(e.target.value)}
              className="h-9 w-full rounded-xl border bg-background px-3 text-sm"
              data-testid="input-periodo"
            />
          </div>

          <Button
            onClick={handleSubmit}
            disabled={loading || (!archivoCompra && !archivoVenta)}
            className="bg-indigo-600 hover:bg-indigo-700 gap-2"
            data-testid="btn-importar"
          >
            <Upload className="h-4 w-4" />
            {loading ? 'Importando…' : 'Importar RCV'}
          </Button>
        </CardContent>
      </Card>

      {/* Resultado */}
      {resultado && (
        <Card data-testid="resultado-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              Importación completa — Período {resultado.periodo || '?'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {renderResumenLado('RCV Compra', resultado.compra)}
            {renderResumenLado('RCV Venta', resultado.venta)}
          </CardContent>
        </Card>
      )}

      {/* Stats por periodo */}
      {stats?.por_periodo_y_tipo?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Provisionales por período</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Período</TableHead>
                  <TableHead>Tipo DTE</TableHead>
                  <TableHead className="text-right">Cantidad</TableHead>
                  <TableHead className="text-right">Monto Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.por_periodo_y_tipo.map((row, idx) => (
                  <TableRow key={`${row.periodo}-${row.tipo_dte}-${idx}`}>
                    <TableCell className="font-mono text-xs">{row.periodo || '—'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">{row.tipo_dte}</Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{row.count}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{fmtCLP(row.monto_total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Historial */}
      {historial.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <History className="h-4 w-4" />
              Historial de imports recientes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Período</TableHead>
                  <TableHead className="text-right">Compra ins.</TableHead>
                  <TableHead className="text-right">Venta ins.</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {historial.map((h, idx) => (
                  <TableRow key={`${h.fecha}-${idx}`} data-testid={`hist-row-${idx}`}>
                    <TableCell className="text-xs">{(h.fecha || '').slice(0, 19).replace('T', ' ')}</TableCell>
                    <TableCell className="text-xs">{h.user}</TableCell>
                    <TableCell className="font-mono text-xs">{h.periodo || '—'}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">
                      {h.compra?.insertados ?? '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs">
                      {h.venta?.insertados ?? '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
